/**
 * Toast region — renders a {@link ToastStore}'s queue in a live region and lets
 * the user dismiss each. Subscribes to the framework-agnostic store and mirrors
 * it into a signal. Token-driven; `danger` toasts assert, others announce
 * politely. The store owns all state; this is pure presentation.
 */
import type { JSX } from 'solid-js';
import { createSignal, For, onCleanup, onMount } from 'solid-js';
import { Icon } from '../components/Icon.tsx';
import { ensureBaseStyles } from '../internal/base-styles.ts';
import { ensureStyles } from '../internal/inject-styles.ts';
import type { Toast, ToastStore } from './store.ts';

const STYLE_ID = 'f4-toast';
const CSS = `
.f4-toast-region {
  position: fixed;
  bottom: var(--space-4);
  right: var(--space-4);
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  max-width: min(24rem, 90vw);
  z-index: var(--z-toast, 600);
}
.f4-toast {
  display: flex;
  align-items: flex-start;
  gap: var(--space-3);
  padding: var(--space-3) var(--space-4);
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border);
  background: var(--color-surface-overlay);
  color: var(--color-text);
  box-shadow: var(--elevation-3);
  animation: f4-slide-up var(--duration-base, 240ms) var(--ease-decelerate, ease);
}
.f4-toast--success { border-color: var(--color-success); }
.f4-toast--warning { border-color: var(--color-warning); }
.f4-toast--danger { border-color: var(--color-danger); }
.f4-toast__body { flex: 1; display: flex; flex-direction: column; gap: var(--space-1); }
.f4-toast__title { font-weight: var(--font-weight-bold); }
.f4-toast__message { color: var(--color-text-muted); }
.f4-toast__action {
  align-self: center;
  padding: var(--space-1) var(--space-3);
  border: none;
  border-radius: var(--radius-sm);
  background: var(--color-accent);
  color: var(--color-accent-contrast);
  font: inherit;
  font-weight: var(--font-weight-semibold);
  white-space: nowrap;
  cursor: pointer;
}
.f4-toast__action:hover { background: var(--color-accent-hover); }
.f4-toast__action { --f4-focus-offset: 1px; }
.f4-toast__close {
  display: inline-flex;
  padding: var(--space-1);
  border: none;
  background: transparent;
  color: var(--color-text-muted);
  border-radius: var(--radius-sm);
  cursor: pointer;
}
.f4-toast__close:hover { color: var(--color-text); }
.f4-toast__close { --f4-focus-offset: 1px; }
@media (prefers-reduced-motion: reduce) { .f4-toast { animation: none; } }
`;

// Close (X) glyph path — decorative, wrapped by Icon.
const CLOSE_PATH =
  'M18.3 5.71 12 12l6.3 6.29-1.42 1.42L10.59 13.4 4.3 19.71 2.89 18.3 9.17 12 2.89 5.71 4.3 4.3l6.29 6.28 6.3-6.28z';

export interface ToastRegionProps {
  /** The store to render. */
  readonly store: ToastStore;
  /** Accessible label for the dismiss button (no bare literal). */
  readonly dismissLabel: string;
}

export function ToastRegion(props: ToastRegionProps): JSX.Element {
  ensureBaseStyles();
  ensureStyles(STYLE_ID, CSS);
  const [toasts, setToasts] = createSignal<readonly Toast[]>(props.store.getAll());

  onMount(() => {
    const unsubscribe = props.store.subscribe((next) => setToasts(next));
    onCleanup(unsubscribe);
  });

  return (
    <div class="f4-toast-region" aria-live="polite" aria-atomic="false">
      <For each={toasts()}>
        {(toast) => (
          <div
            class={`f4-toast f4-toast--${toast.variant}`}
            role={toast.variant === 'danger' ? 'alert' : 'status'}
          >
            <div class="f4-toast__body">
              {toast.title ? <span class="f4-toast__title">{toast.title}</span> : null}
              <span class="f4-toast__message">{toast.message}</span>
            </div>
            {toast.action ? (
              <button
                type="button"
                class="f4-toast__action f4-focus-ring"
                onClick={() => toast.action?.onClick()}
              >
                {toast.action.label}
              </button>
            ) : null}
            <button
              type="button"
              class="f4-toast__close f4-focus-ring"
              aria-label={props.dismissLabel}
              onClick={() => props.store.dismiss(toast.id)}
            >
              <Icon path={CLOSE_PATH} size="sm" />
            </button>
          </div>
        )}
      </For>
    </div>
  );
}
